<script>
  import { onDestroy, createEventDispatcher } from 'svelte';
  import Notification from '@utils/notification';
  import Modal from '@components/Modal.svelte';
  import Input from '@components/Input.svelte';
  import BookmarkCollectionAjax from '@ajax/bookmark_collection';
  import EmojiSelectorModal from '@app-components/modal/EmojiSelectorModal.svelte';
  import Fuse from 'fuse.js';

  import { getGlobalStore } from '@stores/global';

  let bookmarkStore = getGlobalStore('bookmarkStore');
  let bookmarkCollectionStore = getGlobalStore('bookmarkCollectionStore');
  let bookmarkCollectionSelectedStore = getGlobalStore('bookmarkCollectionSelectedStore');

  const dispatch = createEventDispatcher();

  export let bookmarkId;
  export let mobile = false;
  export let showModal = false;
  export let useSelectedCollection = false;

  let bookmarkCollections = [];
  let bookmark = bookmarkStore.find(bookmarkId);
  let selectedCollectionId = null;
  let showEmojiModal = false;
  let createNewEnabled = false;
  let originalCollectionIds = bookmark.collection_ids;
  let collectionIds = bookmark.collection_ids;
  let searched = false;
  let groupCollectionSearchEl;

  let unsubCollections = bookmarkCollectionStore.subscribe((x) => {
    bookmarkCollections = x;
  });

  let unsubBookmark = bookmark.subscribe((x) => (bookmark = x));

  let loading = false;
  let groupCollectionInputEl;
  let selectedEmoji = null;
  let openEmojiSelector = function () {
    showEmojiModal = true;
  };
  let closeEmojiSelector = function () {
    showEmojiModal = false;
  };
  let selectEmoji = async function (emoji) {
    selectedEmoji = emoji;
  };
  let searchGroupCollections = function () {
    if (groupCollectionSearchEl.value === '') {
      bookmarkCollections = bookmarkCollectionStore;
      searched = false;
      return;
    }

    let fuse = new Fuse(bookmarkCollections, {
      keys: ['name'],
      threshold: 0.25,
    });

    bookmarkCollections = fuse.search(groupCollectionSearchEl.value).map((x) => x.item);

    searched = true;
  };

  let bindWindowEvents = function () {
    window.undoCollectionMove = async function () {
      BookmarkCollectionAjax.removeBookmarkFromCollection(selectedCollectionId, bookmarkId);
      dispatch('complete');
      Notification.send({
        text: `<div class="bookmark-saved-notification"><img class='bookmark-saved-checkmark' src='/images/save-button/check.png' /><div class='main-text'>List move reverted</div></div>`,
        layout: mobile ? 'topCenter' : 'bottomLeft',
        timeout: 3500,
      });
    };
  };

  let handleBlurred = async function () {
    let name = groupCollectionInputEl.value;
    if (name == '' || name == null || name == undefined) {
      createNewEnabled = false;
    }
  };

  let createNewCollection = async function () {
    let name = groupCollectionInputEl.value;
    if (name == '' || name == null || name == undefined) {
      Notification.send({
        text: 'Please enter a folder name',
        type: 'error',
      });
      return;
    }
    let resp = await BookmarkCollectionAjax.create({
      name: name,
      emoji_id: selectedEmoji ? selectedEmoji.id : null,
      bookmark_id: bookmarkId,
    });
    selectedCollectionId = resp.bookmark_collection_id;
    collectionIds = [...collectionIds, selectedCollectionId];
    bookmarkCollectionStore.set(await BookmarkCollectionAjax.get());
    dispatch('complete');
    createNewEnabled = false;
    setTimeout(() => bindWindowEvents(), 0);
    displayNotification(name);
  };

  let displayNotification = async function (name, operation = 'add') {
    const text = operation == 'add' ? `Added bookmark to ${name}` : `Removed bookmark from ${name}`;
    Notification.send({
      text: `<div style="display: flex; align-items: center;"><span style="background: #2CAF87; border-radius: 999px; width: 20px; height: 20px; display: inline-flex; align-items: center; justify-content: center; margin-right: 10px;"><i class="fa fa-check" style="color: white; padding-left: 2px; padding-top: -1px;"></i></span><span style="flex: 1">${text}</span></div>`,
      layout: 'topCenter',
      timeout: 3500,
    });
  };

  let actionPerformed = function () {
    collectionIds = collectionIds.sort();
    originalCollectionIds = originalCollectionIds.sort();

    return !(
      collectionIds.length == originalCollectionIds.length &&
      collectionIds.every((val, index) => val === originalCollectionIds[index])
    );
  };

  let hideModal = function () {
    if (useSelectedCollection && actionPerformed()) {
      bookmarkCollectionSelectedStore.set({ collection: collectionIds, manageBookmarksShown: true });
    }
    showModal = false;
    dispatch('close');
  };

  let selectGroupCollection = async function (gc) {
    const operation = collectionIds.indexOf(gc.id) >= 0 ? 'delete' : 'add';
    let resp = null;
    if (operation == 'delete') {
      resp = await BookmarkCollectionAjax.removeBookmarkFromCollection(gc.id, bookmarkId);
    } else {
      resp = await BookmarkCollectionAjax.addBookmarkToCollection(gc.id, bookmarkId);
    }

    selectedCollectionId = resp.bookmark_collection_id;
    if (operation == 'delete') {
      collectionIds = collectionIds.filter((item) => item !== selectedCollectionId);
    } else {
      collectionIds = [...collectionIds, selectedCollectionId];
    }

    dispatch('complete');
    displayNotification(gc.name, operation);
  };

  onDestroy(() => {
    unsubBookmark();
    unsubCollections();
  });
</script>

<style style="text/scss" lang="scss">
  .save-modal {
    margin-top: -30px;
    padding-left: 16px;
    padding-right: 16px;

    .save-modal-header {
      font-weight: 500;
      font-size: 24px;
      line-height: 32px;
      text-align: center;
    }

    .save-modal-subheader {
      margin-top: 12px;
      color: var(--Neutral-Gray, #7f7f7f);
      text-align: center;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 150% */
    }

    .search-search-bar {
      margin-top: 24px;
      position: relative;
    }
    .save-modal-lists {
      margin-top: 24px;
      max-height: 260px;
      overflow: auto;
      overflow-x: hidden;
      margin-left: -5px;
      margin-right: -5px;
      padding-left: 5px;
      padding-right: 5px;
      &.create-new-enabled {
        margin-top: 17px;
      }

      .save-modal-lists-label {
        font-weight: normal;
        font-size: 14px;
        line-height: 20px;
        color: #7f7f7f;
        margin-bottom: 12px;
      }
    }

    .add-new-section-container {
      margin-top: 14px;
      position: relative;
      margin-bottom: -28px;

      img {
        position: absolute;
        right: 15px;
        top: 15px;
        cursor: pointer;
      }
    }

    .save-modal-item-selected {
      background: #f0eaff;
    }

    .save-modal-item {
      border-radius: 12px;
      display: flex;
      height: 48px;
      align-items: center;
      padding-left: 20px;
      padding-right: 20px;
      cursor: pointer;
      margin-bottom: 8px;
      transition: 0.1s all;
      box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.08);

      &:hover {
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
      }

      img {
        width: 24px;
        height: 24px;
        margin-right: 12px;
      }

      .save-modal-item-name {
        text-overflow: ellipsis;
        -webkit-line-clamp: 1;
        display: block;
        overflow: hidden;
        white-space: nowrap;
      }

      .save-modal-section-count {
        display: flex;
        align-items: center;

        span {
          font-size: 12px;
          line-height: 14px;
          opacity: 0.5;
          margin-top: 3px;
        }

        img {
          width: 16px;
          height: 16px;
          margin-right: 8px;
        }
      }

      span {
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
        flex: 1;
      }
    }

    .selected-group {
      background: #fff4f6;
      border: 2px solid #fff4f6;
      margin-top: 24px;
      cursor: unset;

      &:hover {
        border: 2px solid #fff4f6;
        box-shadow: unset;
      }

      .selected-group-cancel-btn {
        width: 40px;
        height: 40px;
        background: white;
        border-radius: 12px;
        align-items: center;
        justify-content: center;
        display: flex;
        margin-right: -13px;
        cursor: pointer;

        &:hover {
          box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
        }
      }
    }

    .create-new-collection-container {
      margin-top: 38px;
      .right-icon {
        position: absolute;
        top: 15px;
        right: 20px;
        cursor: pointer;
        img {
          width: 16px;
          height: 16px;
          margin-right: 8px;
        }
      }
    }

    .create-new-button {
      border: 2px solid #ececec;
      border-radius: 12px;
      display: flex;
      height: 48px;
      align-items: center;
      padding-left: 20px;
      padding-right: 20px;
      cursor: pointer;
      transition: 0.1s all;
      margin-top: 40px;

      &:hover {
        border: 2px solid transparent;
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
      }

      span {
        flex: 1;
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
        color: #a893f9;
      }
    }

    .group-emoji {
      width: 20px;
      height: 20px;
    }
  }

  .mobile {
    font-size: 14px !important;
  }

  .mobile-modal {
    padding-left: 0px !important;
    padding-right: 0px !important;

    .save-modal-lists {
      max-height: 270px;
      margin-top: 37px;
      &.create-new-enabled {
        margin-top: 30px;
      }
    }
    .save-modal-item {
      margin-bottom: 11px;
    }
    .see-all-bookmarks {
      margin-top: 30px;
    }
    .create-new-collection-container {
      margin-top: 22px;
    }
    .create-new-button {
      margin-top: 24px;
    }
  }

  .see-all-bookmarks {
    margin-top: 70px;
    cursor: pointer;
    text-align: center;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    a {
      color: var(--purple-purple, #ae94f9);
      &:hover {
        color: #7054dc !important;
      }
    }
  }

  * :global(.overlay-aligned-top) {
    align-items: flex-start !important;
    padding-top: calc(50vh - 320px);
  }

  * :global(.mobile-add-collection-to-bookmark.modal__container) {
    border-bottom-left-radius: 0px !important;
    border-bottom-right-radius: 0px !important;
  }
</style>

<div class="svelte-modal">
  {#if showEmojiModal}
    <EmojiSelectorModal
      bind:show={showEmojiModal}
      on:emoji-selected={(e) => {
        closeEmojiSelector();
        selectEmoji(e.detail.emoji);
      }} />
  {/if}
  <Modal
    style={mobile
      ? 'margin-bottom: 0px !important; position: absolute !important; width:100%; bottom: 0px !important;'
      : 'width: 490px;'}
    mobile={mobile}
    overlayClass={mobile ? '' : 'overlay-aligned-top'}
    show={showModal}
    containerClass={mobile ? 'mobile-add-collection-to-bookmark' : 'desktop-add-collection-to-bookmark'}
    on:close={hideModal}>
    <div slot="content" class="save-modal" class:mobile-modal={mobile}>
      {#if useSelectedCollection}
        <div class="save-modal-header">Bookmark saved!</div>
        <div class="save-modal-subheader">Want to add it to a folder as well?</div>
      {:else}
        <div class="save-modal-header">Add to folder</div>
      {/if}
      {#if loading}
        <div style="text-align: center; opacity: 0.7; color: #1a1a1a; margin-top: 35px;">
          <i class="fa fa-spin fa-spinner" />
          <span style="margin-left: 8px;">Loading</span>
        </div>
      {:else}
        {#if searched || (bookmarkCollections && bookmarkCollections.length > 3)}
          <div class="search-search-bar">
            <i class="fa fa-search" style="position: absolute; z-index: 1; top: 17px; left: 17px; color: #A893F9;" />
            <Input
              bind:inputEl={groupCollectionSearchEl}
              style="height: 48px;"
              inputStyle="padding-left: 44px;"
              placeholder="Search for a folder"
              on:keyup={searchGroupCollections}
              focus={false} />
          </div>
        {/if}
        <div
          class="save-modal-lists purple-scrollbar"
          class:create-new-enabled={createNewEnabled}
          class:save-modal-lists-scrolled={!mobile && bookmarkCollections.length > 4}>
          {#each bookmarkCollections as gc (gc.id)}
            <div
              class="save-modal-item"
              class:save-modal-item-selected={collectionIds.indexOf(gc.id) >= 0}
              on:click|preventDefault|stopPropagation={() => selectGroupCollection(gc)}>
              <img src={gc.emoji_url} />
              <span class="save-modal-item-name">{gc.name}</span>
              <div class="save-modal-section-count">
                {#if collectionIds.indexOf(gc.id) >= 0}
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z"
                      fill="#AE94F9" />
                    <path
                      d="M4.5 7.85294L6.92308 10.5L11.5 5.5"
                      stroke="white"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                {:else}
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z"
                      stroke="#AE94F9"
                      stroke-width="2" />
                  </svg>
                {/if}
              </div>
            </div>
          {/each}
        </div>
        {#if createNewEnabled}
          <div class="create-new-collection-container" style="position: relative;">
            <img
              on:click={() => {
                openEmojiSelector();
              }}
              src={selectedEmoji ? selectedEmoji.outside_src : '/emojis/folder.png'}
              style="cursor: pointer; position: absolute; top: 14px; left: 20px; z-index: 1; width: 20px; height: 20px;" />
            <Input
              bind:inputEl={groupCollectionInputEl}
              on:blur={handleBlurred}
              placeholder="My new folder"
              inputStyle="padding-left: 52px; padding-right: 42px;"
              on:enter={createNewCollection}
              focus={true} />
            <div class="right-icon" on:click={createNewCollection}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M8.32911 1.32911C8.32911 0.595065 7.73405 0 7 0C6.26595 0 5.67089 0.595065 5.67089 1.32911V5.67089H1.32911C0.595065 5.67089 0 6.26595 0 7C0 7.73405 0.595065 8.32911 1.32911 8.32911H5.67089V12.6709C5.67089 13.4049 6.26595 14 7 14C7.73405 14 8.32911 13.4049 8.32911 12.6709V8.32911H12.6709C13.4049 8.32911 14 7.73405 14 7C14 6.26595 13.4049 5.67089 12.6709 5.67089H8.32911V1.32911Z"
                  fill="#AE94F9" />
              </svg>
            </div>
          </div>
        {:else}
          <div
            class="create-new-button"
            on:click={() => {
              createNewEnabled = true;
            }}>
            <span>Create new folder</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M8.32911 1.32911C8.32911 0.595065 7.73405 0 7 0C6.26595 0 5.67089 0.595065 5.67089 1.32911V5.67089H1.32911C0.595065 5.67089 0 6.26595 0 7C0 7.73405 0.595065 8.32911 1.32911 8.32911H5.67089V12.6709C5.67089 13.4049 6.26595 14 7 14C7.73405 14 8.32911 13.4049 8.32911 12.6709V8.32911H12.6709C13.4049 8.32911 14 7.73405 14 7C14 6.26595 13.4049 5.67089 12.6709 5.67089H8.32911V1.32911Z"
                fill="#AE94F9" />
            </svg>
          </div>
        {/if}
      {/if}
    </div>
  </Modal>
</div>
